import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { useOrg } from '../lib/org';
import { Layout, Topbar } from '../components/Layout';

type Aged = { not_due: number; d1_30: number; d31_60: number; d61_90: number; d90_plus: number; total: number } | null;
type Cash = { name: string; account_type: string; currency: string; balance: number };
type Margin = { service_line_name?: string; property_type?: string; revenue: number; expense: number };
type Prof = { contract_name: string; margin: number; margin_pct: number | null; below_threshold: boolean };

export function FinanceDashboard() {
  const { t, i18n } = useTranslation();
  const { currency } = useOrg();
  const loc = i18n.language;
  const money = (n: number) => formatCurrency(n, currency, loc);
  const [cash, setCash] = useState<Cash[]>([]);
  const [ar, setAr] = useState<Aged>(null);
  const [ap, setAp] = useState<Aged>(null);
  const [bySl, setBySl] = useState<Margin[]>([]);
  const [byPt, setByPt] = useState<Margin[]>([]);
  const [prof, setProf] = useState<Prof[]>([]);

  const load = useCallback(async () => {
    const [c, r, p, sl, pt, pc] = await Promise.all([
      supabase.from('v_cash_position').select('name,account_type,currency,balance'),
      supabase.from('v_aged_receivables').select('*').maybeSingle(),
      supabase.from('v_aged_payables').select('*').maybeSingle(),
      supabase.from('v_margin_by_service_line').select('service_line_name,revenue,expense'),
      supabase.from('v_margin_by_property_type').select('property_type,revenue,expense'),
      supabase.from('v_profitability_contract').select('contract_name,margin,margin_pct,below_threshold').order('margin', { ascending: false }).limit(8),
    ]);
    setCash((c.data as any) ?? []); setAr((r.data as any) ?? null); setAp((p.data as any) ?? null);
    setBySl((sl.data as any) ?? []); setByPt((pt.data as any) ?? []); setProf((pc.data as any) ?? []);
  }, []);
  useEffect(() => { void load(); }, [load]);

  const totalCash = cash.reduce((s, c) => s + Number(c.balance), 0);
  const buckets: (keyof NonNullable<Aged>)[] = ['not_due', 'd1_30', 'd31_60', 'd61_90', 'd90_plus'];

  const AgedTable = ({ title, data }: { title: string; data: Aged }) => (
    <Card className="overflow-hidden p-0">
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5 text-sm font-medium"><span>{title}</span><span className="font-mono">{money(Number(data?.total ?? 0))}</span></div>
      <div className="grid grid-cols-5 gap-px bg-divider text-center">
        {buckets.map((b) => (
          <div key={b} className="bg-card px-2 py-2.5">
            <div className="text-2xs uppercase text-muted">{t('fd.' + b)}</div>
            <div className="mt-0.5 font-mono text-xs">{money(Number(data?.[b] ?? 0))}</div>
          </div>
        ))}
      </div>
    </Card>
  );

  const MarginBars = ({ title, rows, label }: { title: string; rows: Margin[]; label: (m: Margin) => string }) => {
    const max = Math.max(1, ...rows.map((r) => Math.abs(Number(r.revenue) - Number(r.expense))));
    return (
      <Card className="p-4">
        <div className="mb-3 text-sm font-medium">{title}</div>
        <div className="space-y-2">
          {rows.map((r, i) => {
            const m = Number(r.revenue) - Number(r.expense);
            return (
              <div key={i} className="text-sm">
                <div className="flex justify-between"><span>{label(r)}</span><span className={cn('font-mono', m < 0 ? 'text-st-overdue-fg' : '')}>{money(m)}</span></div>
                <div className="mt-1 h-1.5 rounded-full bg-bg"><div className={cn('h-1.5 rounded-full', m < 0 ? 'bg-st-overdue-fg' : 'bg-accent-dark')} style={{ width: `${(Math.abs(m) / max) * 100}%` }} /></div>
              </div>
            );
          })}
          {rows.length === 0 && <div className="text-xs text-muted">{t('common.noData')}</div>}
        </div>
      </Card>
    );
  };

  return (
    <Layout header={<Topbar />}>
      <div className="px-7 py-6">
        <h1 className="mb-1 text-xl font-semibold">{t('nav.financeDash')}</h1>
        <p className="mb-5 text-sm text-muted">{t('fd.subtitle')}</p>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[['cash', totalCash], ['receivables', Number(ar?.total ?? 0)], ['payables', Number(ap?.total ?? 0)], ['netCash', totalCash - Number(ap?.total ?? 0)]].map(([k, v]) => (
            <Card key={k as string} className="p-4"><div className="text-xs uppercase tracking-wide text-muted">{t('fd.' + k)}</div><div className="mt-1 text-xl font-semibold">{money(v as number)}</div></Card>
          ))}
        </div>

        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <AgedTable title={t('fd.agedReceivables')} data={ar} />
          <AgedTable title={t('fd.agedPayables')} data={ap} />
        </div>

        <div className="mb-6 grid gap-4 lg:grid-cols-2">
          <MarginBars title={t('fd.marginByServiceLine')} rows={bySl} label={(r) => r.service_line_name ?? '—'} />
          <MarginBars title={t('fd.marginByPropertyType')} rows={byPt} label={(r) => t('propType.' + r.property_type, r.property_type ?? '—')} />
        </div>

        <Card className="overflow-hidden p-0">
          <div className="border-b border-border px-4 py-2.5 text-sm font-medium">{t('fd.topContracts')}</div>
          <div className="divide-y divide-divider">
            {prof.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-2 text-sm">
                <span>{p.contract_name}</span>
                <div className="flex items-center gap-3">
                  <span className={cn('font-mono', Number(p.margin) < 0 ? 'text-st-overdue-fg' : '')}>{money(Number(p.margin))}</span>
                  {p.margin_pct != null && <span className={cn('rounded-pill px-2 py-0.5 text-2xs', p.below_threshold ? 'bg-st-overdue-bg text-st-overdue-fg' : 'bg-bg text-muted')}>{p.margin_pct}%</span>}
                </div>
              </div>
            ))}
            {prof.length === 0 && <div className="px-4 py-6 text-center text-muted">{t('common.noData')}</div>}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
